import { colors } from "@constants/theme";

import React from "react";
import { StyleSheet, View, TouchableOpacity, Dimensions } from "react-native";
import CustomText from "@components/CustomText";
import CallIcon from "@assets/svg/newCall";
import NoBag from "@assets/svg/NoBag";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import { CP_DB_EVENTS } from "@constants/analyticsEventsCT";
import IssueTags from "@components/IssueTags";
import Tag from "@components/Tag";

function CustomerCard({ item, navigation, route, createCall }) {
  const { spoke, date } = route.params;
  const callCustomer = (phoneNumber) => {
    if (createCall) {
      createCall(phoneNumber);
    } else {
      handleCallButtonPress(phoneNumber, CP_DB_EVENTS.CUSTOMERS_CALLED, {
        from: "Sorting Deliveries",
      });
    }
  };

  const generateAvatar = (name) => {
    const initials = name?.charAt(0).toUpperCase();
    return (
      <View style={styles.avatarContainer}>
        <CustomText variant="subtitle_16" style={styles.avatarText}>
          {initials || "U"}
        </CustomText>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={styles.headerContent}>
          {generateAvatar(item?.db_name)}
          <View>
            <CustomText
              variant="body_text_3"
              style={{
                color: colors.mediumBlacklightBlack,
                top: 5,
              }}
            >
              {item?.route_name}
            </CustomText>

            <CustomText
              variant="subtitle_16"
              style={{
                color:
                  item?.db_name === "UNASSIGN"
                    ? colors.mediumBlacklightBlack
                    : colors.black,
              }}
            >
              {item?.db_name || "Unassigned"}
            </CustomText>
          </View>
        </View>
        <View style={styles.bagsContainer}>
          {Array.isArray(item?.bags) &&
          item?.status === "PACKED" &&
          item?.bags.every((bag) => bag === "") ? (
            <NoBag />
          ) : (
            item?.bags?.map((i, index) => (
              <CustomText
                key={`bag-${index}`}
                variant="heading_24"
                numberOfLines={1}
                style={
                  index === item?.bags?.length - 1
                    ? styles.bagTextLast
                    : styles.bagText
                }
              >
                {index === item?.bags?.length - 1 ? i : `${i}, `}
              </CustomText>
            ))
          )}
        </View>
      </View>
      <View style={styles.contentContainer}>
        <View style={styles.contentHeader}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <CustomText
              variant="title_16"
              style={{
                maxWidth: Dimensions.get("window").width * 0.6,
              }}
            >
              {item?.cx_name}
            </CustomText>
            {item?.is_priority_cx && (
              <View style={{ marginLeft: 6 }}>
                <Tag text="Priority" />
              </View>
            )}
          </View>
          <TouchableOpacity
            testID="sd-call-customer"
            onPress={() => callCustomer(item?.cx_phone)}
          >
            <CallIcon strokeColor="#2195F2" width={18} height={18} />
          </TouchableOpacity>
        </View>
        <View style={styles.addressContainer}>
          <CustomText variant="body_text_3" style={styles.addressText}>
            {item?.cx_formatted_address}
          </CustomText>
        </View>
        <View style={styles.issueTagsContainer}>
          <IssueTags current_state={item?.state} />
        </View>
        {item?.reissue_txt ? (
          <View style={styles.warningContainer}>
            <CustomText variant="caption_2" style={styles.warningText}>
              {item?.reissue_txt}
            </CustomText>
          </View>
        ) : null}
        <TouchableOpacity
          testID="sd-see-items"
          activeOpacity={0.6}
          onPress={() => {
            navigation.navigate("SortingItemList", {
              user_name: item?.cx_name,
              orders_list: item?.order_ids,
              user_id: item?.cx_id,
              user_phone: item?.cx_phone,
              date,
              spokeName: spoke,
              type: route.params?.crate?.type,
              db_name: item?.db_name,
              bags: item?.bags,
              route_name: item?.route_name,
              route_id: item?.route_id,
              crate: route.params?.crate,
              shipment_ids: item?.shipment_ids,
              unique_key: item?.unique_key,
              status: item?.status,
              bags_disabled: item?.bags_disabled,
              cx_packing_enabled: item?.cx_packing_enabled,
              fragile_order_ids: item?.fragile_order_ids,
              top_sku_order_ids: item?.top_sku_order_ids,
            });
          }}
          style={[
            styles.seeItemsButton,
            item?.button_info?.button_type !== "Primary"
              ? {
                  backgroundColor: colors.white,
                  borderColor: "rgba(0, 0, 0, 0.20)",
                  borderWidth: 1,
                }
              : null,
          ]}
        >
          <CustomText
            variant="title_16"
            style={[
              styles.seeItemsButtonText,
              item?.button_info?.button_type !== "Primary"
                ? { color: colors.black }
                : null,
            ]}
          >
            {item?.button_info?.button_text}
          </CustomText>
        </TouchableOpacity>
      </View>
    </View>
  );
}

export default CustomerCard;

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 14,
    marginVertical: 10,
    backgroundColor: colors.white,
    elevation: 1,
    borderRadius: 10,
  },
  headerContainer: {
    backgroundColor: "#F4FAF5",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    padding: 16,
  },
  headerContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  bagsContainer: {
    flexDirection: "row",
    alignItems: "center",
    maxWidth: "50%",
    overflow: "hidden",
    flexWrap: "wrap",
  },
  bagText: {
    fontSize: 24,
  },
  bagTextLast: {
    fontSize: 24,
  },
  avatarContainer: {
    height: 35,
    width: 35,
    borderRadius: 20,
    marginRight: 8,
    backgroundColor: colors.black,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: colors.white,
  },
  contentContainer: {
    padding: 16,
    paddingTop: 8,
  },
  contentHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  addressContainer: {
    maxWidth: "80%",
    marginVertical: 2,
  },
  addressText: {
    color: colors.mediumBlacklightBlack,
  },
  seeItemsButton: {
    backgroundColor: colors.plain_green,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginTop: 12,
    alignItems: "center",
  },
  seeItemsButtonText: {
    color: colors.white,
  },
  issueTagsContainer: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  warningContainer: {
    backgroundColor: colors.warning_yellow || "#FFF3CD",
    padding: 6,
    borderRadius: 6,
    marginTop: 8,
  },
  warningText: {
    color: colors.black,
    textAlign: "center",
  },
});
